import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Search, Instagram, User, LogOut } from "lucide-react";
import { BRAND, LOGO_URL, CATEGORIES } from "@/lib/brand";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerAuthContext";

const linkBase = "text-[13px] tracking-[0.18em] uppercase font-medium transition-colors";
const inactive = "text-sj-ink hover:text-sj-goldDark";
const active   = "text-sj-goldDark";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [search, setSearch] = useState("");
  const { totals } = useCart();
  const { customer, signInWithGoogle, logout } = useCustomer();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => { setOpen(false); setUserMenu(false); }, [loc.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    nav(`/shop?q=${encodeURIComponent(search.trim())}`);
    setSearch("");
  };

  return (
    <>
      <div className="w-full bg-sj-ink text-white overflow-hidden" data-testid="announcement-bar">
        <div className="marquee py-2 text-[11px] tracking-[0.35em] uppercase">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-16 pr-16">
              <span>✦ Free shipping over ₹1499</span>
              <span>✦ Hand-crafted in India</span>
              <span>✦ Cash-safe checkout via Razorpay</span>
              <span>✦ New arrivals every week</span>
              <span>✦ Star Jewels — a little starlight</span>
            </div>
          ))}
        </div>
      </div>

      <header className="glass-nav sticky top-0 z-50" data-testid="navbar">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-10">
          <div className="flex items-center justify-between h-[76px]">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img src={LOGO_URL} alt="Star Jewels" className="h-16 w-16 sm:h-20 sm:w-20 object-contain shimmer" />
              <div className="leading-tight">
                <div className="font-display text-[19px] font-medium tracking-wide text-sj-ink">STAR JEWELS</div>
                <div className="overline text-[9px]">Fine fashion jewellery</div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-9">
              <NavLink to="/" end className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-home">Home</NavLink>
              {CATEGORIES.slice(0, 3).map((c) => (
                <NavLink
                  key={c.key}
                  to={`/shop?category=${c.key}`}
                  className={`${linkBase} ${inactive}`}
                  data-testid={`nav-cat-${c.key}`}
                >
                  {c.label}
                </NavLink>
              ))}
              <NavLink to="/about" className={({isActive}) => `${linkBase} ${isActive ? active : inactive}`} data-testid="nav-about">About</NavLink>
            </nav>

            <div className="flex items-center gap-2 sm:gap-4">
              <form onSubmit={submitSearch} className="hidden md:flex items-center bg-sj-cream/70 rounded-full px-4 h-10 w-[220px]">
                <Search size={15} className="text-sj-muted" />
                <input
                  data-testid="nav-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search jewelry..."
                  className="bg-transparent w-full pl-2 text-[13px] focus:outline-none placeholder:text-sj-muted"
                />
              </form>
              <Link to="/cart" className="relative p-2 group" data-testid="nav-cart-icon">
                <ShoppingBag size={20} className="text-sj-ink group-hover:text-sj-goldDark transition-colors" />
                {totals.count > 0 && (
                  <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-sj-gold text-white text-[10px] font-semibold flex items-center justify-center">
                    {totals.count}
                  </span>
                )}
              </Link>

              {/* Customer sign-in / avatar */}
              {customer ? (
                <div className="relative hidden sm:block">
                  <button
                    onClick={() => setUserMenu((v) => !v)}
                    className="flex items-center gap-2 p-1 rounded-full hover:ring-2 hover:ring-sj-gold/30 transition-all"
                    data-testid="nav-user-avatar"
                  >
                    {customer.picture ? (
                      <img src={customer.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-sj-gold/20 flex items-center justify-center text-sj-goldDark text-sm font-medium">
                        {customer.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                  </button>
                  {userMenu && (
                    <div className="absolute right-0 top-12 w-56 bg-white shadow-xl rounded-md border border-sj-ink/10 py-2 z-[60]" data-testid="nav-user-menu">
                      <div className="px-4 py-2 border-b border-sj-ink/10">
                        <div className="font-medium text-sm truncate">{customer.name}</div>
                        <div className="text-xs text-sj-muted truncate">{customer.email}</div>
                      </div>
                      <Link to="/account" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-sj-cream" data-testid="nav-user-account">
                        <User size={14} /> My account
                      </Link>
                      <button
                        onClick={async () => { await logout(); nav("/"); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-sj-cream text-sj-rose"
                        data-testid="nav-user-logout"
                      >
                        <LogOut size={14} /> Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={signInWithGoogle}
                  className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-full border border-sj-ink/15 hover:border-sj-goldDark hover:text-sj-goldDark text-[13px] transition-colors"
                  data-testid="nav-signin-btn"
                >
                  <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 5.9 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 5.9 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.3-11.3-8L6.2 32.9C9.5 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2c-.4.4 6.7-4.9 6.7-14.8 0-1.2-.1-2.3-.5-3.5z"/></svg>
                  Sign in
                </button>
              )}

              <button
                data-testid="nav-mobile-toggle"
                onClick={() => setOpen(true)}
                className="lg:hidden p-2 text-sj-ink"
                aria-label="Open menu"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[70] transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!open}
      >
        <div className="absolute inset-0 bg-sj-ink/50" onClick={() => setOpen(false)} />
        <aside
          className={`absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-2xl transition-transform duration-500 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
          data-testid="mobile-drawer"
        >
          <div className="flex items-center justify-between p-5 border-b border-sj-gold/20">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} className="h-14 w-14 object-contain" alt="" />
              <span className="font-display font-medium tracking-wide">STAR JEWELS</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-2" data-testid="mobile-drawer-close" aria-label="Close">
              <X size={22} />
            </button>
          </div>
          <form onSubmit={submitSearch} className="mx-5 mt-5 mb-3 flex items-center bg-sj-cream rounded-full px-4 h-11">
            <Search size={16} className="text-sj-muted" />
            <input
              data-testid="mobile-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search jewelry..."
              className="bg-transparent w-full pl-2 text-sm focus:outline-none"
            />
          </form>
          <nav className="p-5 flex flex-col gap-1">
            {[
              { to: "/", label: "Home", id: "home" },
              ...CATEGORIES.map((c) => ({ to: `/shop?category=${c.key}`, label: c.label, id: c.key })),
              { to: "/about", label: "About / Contact", id: "about" },
            ].map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`mobile-nav-${l.id}`}
                className={({isActive}) => `py-3 border-b border-sj-gold/10 font-display text-[15px] tracking-wide ${isActive ? "text-sj-goldDark" : "text-sj-ink"}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-5 flex items-center gap-3">
            {customer ? (
              <>
                <Link to="/account" className="btn-outline text-sm flex-1 justify-center" data-testid="mobile-account-btn">
                  <User size={14} /> My account
                </Link>
                <button onClick={async () => { await logout(); }} className="p-3 rounded-full bg-sj-cream" data-testid="mobile-logout-btn">
                  <LogOut size={16} className="text-sj-rose" />
                </button>
              </>
            ) : (
              <button onClick={signInWithGoogle} className="btn-primary text-sm flex-1 justify-center" data-testid="mobile-signin-btn">
                Sign in with Google
              </button>
            )}
            <a href={`https://instagram.com/${BRAND.instagram}`} target="_blank" rel="noreferrer" className="p-3 rounded-full bg-sj-cream" data-testid="mobile-instagram-btn">
              <Instagram size={18} className="text-sj-goldDark" />
            </a>
          </div>
        </aside>
      </div>
    </>
  );
}
